/**
 * Computes the inverse of the cumulative density function (CDF) for the
 * Student's t distribution.
 *
 * Instead of bringing in a dependency to compute this for arbitrary parameters,
 * this function hardcodes return values for the small set of parameters we'll
 * need to handle. (The degrees of freedom parameter is static for each survey,
 * and we expect to have 2-4 surveys. The area under the curve is derived from
 * the size of the confidence interval, and we expect to give the user no more
 * than 3 options for the size of the confidence interval.) The hardcoded values
 * can be found using any of several statistical tools, such as:
 *
 * - Python's scipy.stats.t.ppf()
 * - R's qt()
 * - JavaScript's jStat.studentt.inv()
 * - JavaScript's @stdlib/stats-base-dists-t-quantile
 *
 * @param {number} area Area under the CDF curve starting from -infinity.
 * @param {number} dof Degrees of freedom.
 * @return {number} The t value such that the area under the CDF curve in the range
 *     (-infinity, t) is equal to the value of the area parameter.
 */
function tInverse (area, dof) {
  if (area === 0.975 && dof === 157) {
    return 1.975189163
  } else if (area === 0.975 && dof === 95) {
    return 1.985251004
  } else if (area === 0.975 && dof === 81) {
    return 1.989686323
  } else {
    throw new Error('Unsupported values for area and dof parameters')
  }
}

/**
 * Computes the asymmetric confidence interval (CI) of a proportion.
 *
 * Because a proportion is bounded in [0, 1], its CI should be as well. This
 * function ensures that the CI is bounded in [0, 1] by allowing it be
 * asymmetric. Values close to 0 or 1 will exhibit the greatest asymmetry.
 *
 * If p is exactly 0 or 1, the confidence intervals are undefined and the
 * function returns null for both the lower and upper bounds.
 *
 * @param {number} p Proportion around which to compute the CI.
 * @param {number} v Variance of the proportion.
 * @param {number} dof Degrees of freedom.
 * @param {number} confidence Confidence level for the interval. For a 95% CI,
 *     this parameter should be 0.95.
 *
 * @return {Object} Lower and upper bounds of the confidence interval.
 */
function computeCiProportion (p, v, dof, confidence) {
  if (p === 0 || p === 1 || p === null) {
    return { lower: null, upper: null }
  }

  const pPrime = Math.log(p) - Math.log(1 - p)
  const sPrime = Math.sqrt(v) / (p * (1 - p))

  const tArea = 1 - ((1 - confidence) / 2)
  const t = tInverse(tArea, dof)

  const lowerPrime = pPrime - t * sPrime
  const upperPrime = pPrime + t * sPrime

  const lower = Math.exp(lowerPrime) / (1 + Math.exp(lowerPrime))
  const upper = Math.exp(upperPrime) / (1 + Math.exp(upperPrime))

  return { lower: lower, upper: upper }
}

/**
 * Computes the confidence interval (CI) of a count.
 *
 * The returned CI is symmetric around the count, except when the lower CI bound
 * is negative, in which case the lower CI bound is set to 0. (Because a count
 * cannot be negative, its lower CI bound should also not be negative.)
 *
 * If the count is 0, the confidence interval is undefined and the function
 * returns null for both the lower and upper bounds.
 *
 * @param {number} x Count around which to compute the CI.
 * @param {number} v Variance of the count.
 * @param {number} dof Degrees of freedom.
 * @param {number} confidence Confidence level for the interval. For a 95% CI,
 *     this parameter should be 0.95.
 *
 * @return {Object} Lower and upper bounds of the confidence interval.
 */
function computeCiCount (x, v, dof, confidence) {
  if (x === 0 || x === null) {
    return { lower: null, upper: null }
  }

  const s = Math.sqrt(v)

  const tArea = 1 - ((1 - confidence) / 2)
  const t = tInverse(tArea, dof)

  const lower = Math.max(x - t * s, 0)
  const upper = x + t * s

  return { lower: lower, upper: upper }
}

/**
 * Computes the relative standard error (RSE) of a proportion.
 *
 * The general equation for the RSE of an estimate X is SE(X) / X. However,
 * because a proportion's properties are symmetric around 0.5, this method uses
 * a modified equation to compute RSEs that are symmetric around 0.5. For
 * example, RSE(0.1) will equal RSE(0.9), assuming the standard errors are the
 * same.
 *
 * If p is exactly 0 or 1, the RSE is undefined and the function returns null.
 *
 * @param {number} p Proportion for which to compute the RSE.
 * @param {number} v Variance of the proportion.
 */
function computeRseProportion (p, v) {
  if (p === 0 || p === 1 || p === null) {
    return null
  }
  if (p < 0.5) {
    return Math.sqrt(v) / p
  }
  return Math.sqrt(v) / (1 - p)
}

/**
 * Initialize a data structure for holding intermediate and output values in the
 * distribution function.
 */
function initDataBuffer (keys1, keys2, value) {
  const buffer = {}
  keys1.forEach(k1 => {
    buffer[k1] = {}
    keys2.forEach(k2 => {
      buffer[k1][k2] = value
    })
  })
  return buffer
}

/**
 * In a Taylor WR Survey, all estimates are computed assuming a weighted,
 * stratified, and clustered design using the Taylor linearization method for
 * variances and assuming clusters were sampled with replacement within each
 * stratum.
 */
export class TaylorWRSurvey {
  /**
   * @param {Array} data Object of arrays containing all the survey data. The data
   *     MUST be sorted by the stratum and cluster variables.
   * @param {string} weightName Name of the survey's weight variable.
   * @param {string} stratumName Name of the survey's stratum variable.
   * @param {string} clusterName Name of the survey's cluster variable.
   * @param {string} ddf Design-based degrees of freedom for the survey.
   */
  constructor (data, weightName, stratumName, clusterName, ddf) {
    this.data = data
    this.weight = data[weightName]
    this.stratum = data[stratumName]
    this.cluster = data[clusterName]
    this.ddf = ddf
  }

  /**
   * Computes the weighted frequency distribution of a variable.
   *
   * The distribution is computed within each level of another variable, called
   * the "by" variable. Only those records appearing in the subpopulation of
   * interest are included.
   *
   * Also computes the variance and 95% confidence interval.
   *
   * @param {string} xName Name of the variable whose distribution will be
   *     computed.
   * @param {Array} xLevels Unique levels of x.
   * @param {string} byName Name of the variable whose levels form the
   *     by-groups, within which independent distributions of x will be
   *     computed.
   * @param {Array} byLevels Unique levels of the by variable.
   * @param {Array} subpopName Name of the boolean variable indicating which
   *     records to include in the analysis.
   *
   * @return {Object} Data structure containing all estimates in the distribution.
   *     Top-level keys are the byLevels. The value for each key is another Object
   *     whose keys are the variable levels. The value for each of those keys is
   *     another Object containing the estimates with keys like 'proportion' and
   *     'ciProportion'.
   */
  distribution (xName, xLevels, byName, byLevels, subpopName) {
    // ---------------------------------------------------------------------------
    // Initialize objects that will hold intermediate calculations for each
    // level.

    const N = initDataBuffer(byLevels, xLevels, 0)
    const n = initDataBuffer(byLevels, xLevels, 0)
    const denom = initDataBuffer(byLevels, xLevels, 0)
    const numer = initDataBuffer(byLevels, xLevels, 0)
    const p = initDataBuffer(byLevels, xLevels, null)

    const zNumer = initDataBuffer(byLevels, xLevels, 0)
    const rNumer = initDataBuffer(byLevels, xLevels, 0)
    const r2Numer = initDataBuffer(byLevels, xLevels, 0)
    const vNumer = initDataBuffer(byLevels, xLevels, 0)

    const zP = initDataBuffer(byLevels, xLevels, 0)
    const rP = initDataBuffer(byLevels, xLevels, 0)
    const r2P = initDataBuffer(byLevels, xLevels, 0)
    const vP = initDataBuffer(byLevels, xLevels, 0)

    // ---------------------------------------------------------------------------
    // Store data arrays in shorter names for cleaner references later.

    const x = this.data[xName]
    const by = this.data[byName]
    const subpop = this.data[subpopName]

    // ---------------------------------------------------------------------------
    // Pass 1: Compute the proportion for each level.

    for (let i = 0; i < x.length; i++) {
      if (by[i] === '' || !subpop[i]) {
        continue
      }
      const byLevel = by[i]
      xLevels.forEach(xLevel => {
        if (x[i] !== '') {
          N[byLevel][xLevel] += 1
          denom[byLevel][xLevel] += this.weight[i]
        }
        if (x[i] === xLevel) {
          n[byLevel][xLevel] += 1
          numer[byLevel][xLevel] += this.weight[i]
        }
      })
    }

    byLevels.forEach(byLevel => {
      xLevels.forEach(xLevel => {
        if (denom[byLevel][xLevel] > 0) {
          p[byLevel][xLevel] = numer[byLevel][xLevel] / denom[byLevel][xLevel]
        }
      })
    })

    // ---------------------------------------------------------------------------
    // Pass 2: Compute the variance of the proportion for each level.

    let h = 0
    let startData = false
    let startStratum = false
    let startCluster = false
    let endData = false
    let endStratum = false
    let endCluster = false

    for (let i = 0; i < x.length; i++) {
      startData = (i === 0)
      startStratum = (startData || this.stratum[i] !== this.stratum[i - 1])
      startCluster = (startStratum || this.cluster[i] !== this.cluster[i - 1])
      endData = (i === x.length - 1)
      endStratum = (endData || this.stratum[i] !== this.stratum[i + 1])
      endCluster = (endStratum || this.cluster[i] !== this.cluster[i + 1])

      if (startStratum) {
        h = 0
        byLevels.forEach(byLevel => {
          xLevels.forEach(xLevel => {
            rNumer[byLevel][xLevel] = 0
            r2Numer[byLevel][xLevel] = 0
            rP[byLevel][xLevel] = 0
            r2P[byLevel][xLevel] = 0
          })
        })
      }

      if (startCluster) {
        h += 1
        byLevels.forEach(byLevel => {
          xLevels.forEach(xLevel => {
            zNumer[byLevel][xLevel] = 0
            zP[byLevel][xLevel] = 0
          })
        })
      }

      if (by[i] !== '' && subpop[i]) {
        const byLevel = by[i]
        xLevels.forEach(xLevel => {
          if (x[i] !== '') {
            zNumer[byLevel][xLevel] += (
              this.weight[i] * (x[i] === xLevel ? 1 : 0)
            )
            zP[byLevel][xLevel] += (
              this.weight[i] * (
                (x[i] === xLevel ? 1 : 0) - p[byLevel][xLevel]
              ) / denom[byLevel][xLevel]
            )
          }
        })
      }

      if (endCluster) {
        byLevels.forEach(byLevel => {
          xLevels.forEach(xLevel => {
            rNumer[byLevel][xLevel] += zNumer[byLevel][xLevel]
            r2Numer[byLevel][xLevel] += Math.pow(zNumer[byLevel][xLevel], 2)
            rP[byLevel][xLevel] += zP[byLevel][xLevel]
            r2P[byLevel][xLevel] += Math.pow(zP[byLevel][xLevel], 2)
          })
        })
      }

      if (endStratum) {
        byLevels.forEach(byLevel => {
          xLevels.forEach(xLevel => {
            vNumer[byLevel][xLevel] += (
              (h * r2Numer[byLevel][xLevel] - Math.pow(rNumer[byLevel][xLevel], 2)) / (h - 1)
            )
            vP[byLevel][xLevel] += (
              (h * r2P[byLevel][xLevel] - Math.pow(rP[byLevel][xLevel], 2)) / (h - 1)
            )
          })
        })
      }
    }

    // ---------------------------------------------------------------------------
    // Create an output object collecting the results from each level.

    const out = initDataBuffer(byLevels, xLevels, null)

    const confidence = 0.95

    byLevels.forEach(byLevel => {
      xLevels.forEach(xLevel => {
        out[byLevel][xLevel] = {
          proportion: p[byLevel][xLevel],
          ciProportion: computeCiProportion(
            p[byLevel][xLevel],
            vP[byLevel][xLevel],
            this.ddf,
            confidence
          ),
          varianceProportion: N[byLevel][xLevel] === 0 ? null : vP[byLevel][xLevel],
          sampleSize: n[byLevel][xLevel],
          unweightedDenominator: N[byLevel][xLevel],
          numerator: numer[byLevel][xLevel],
          ciNumerator: computeCiCount(
            numer[byLevel][xLevel],
            vNumer[byLevel][xLevel],
            this.ddf,
            confidence
          ),
          varianceNumerator: N[byLevel][xLevel] === 0 ? null : vNumer[byLevel][xLevel],
          denominator: denom[byLevel][xLevel],
          rseProportion: computeRseProportion(
            p[byLevel][xLevel],
            vP[byLevel][xLevel]
          )
        }
      })
    })

    return out
  }
}
